// Words for the exam's Word (its dialog, the documents list and the exam form): the job's stages and errors, the
// warnings and their places, the languages and the footer's variant phrase.
import { getApiErrorCode } from "../../../utils/apiError";

// The footer's word for the variant code, in the Word's own language (not the page's): the document's own words, as
// the server prints them («НҰСҚА – 3», «ВАРИАНТ – 3»). A Word without a language prints Russian.
const VARIANT_WORD = { kk: "НҰСҚА", ru: "ВАРИАНТ" };
export const printLanguage = (language) => (language === "kk" ? "kk" : "ru");
export const variantWord = (language) => VARIANT_WORD[printLanguage(language)];
export const footerPhrase = (language, code) => `${variantWord(language)} – ${code}`;

// The heading as the Word prints it: in capitals, by the Word's language rules.
export const printedHeading = (heading, language) =>
  (heading || "").toLocaleUpperCase(printLanguage(language) === "kk" ? "kk-KZ" : "ru-RU");

// "Қазақша" (capitalized, a value) or "қазақша" (lower case, inside a line of the documents list).
export const languageName = (t, language, { inline = false } = {}) =>
  t(`exam_bank.${inline ? "documents" : "word"}.language_${printLanguage(language)}`);

export const isLiveJob = (job) => job?.status === "queued" || job?.status === "running";

const STAGES = ["queued", "waiting_builder", "waiting_memory", "retrying", "images", "math", "building", "saving", "done"];
export const stageLabel = (t, stage) =>
  STAGES.includes(stage) ? t(`exam_bank.word.stage.${stage}`) : t("exam_bank.word.stage.unknown");

// Why a job failed: the shared texts of its code (errors.<code>), the server's own words for a code the page does
// not know (error_detail is the fallback, not the first choice: it is Russian).
const JOB_ERRORS = [
  "server_busy",
  "build_timeout",
  "build_too_large",
  "storage_not_private",
  "storage_unchecked",
  "worker_lost",
  "enqueue_failed",
];
export const jobErrorText = (t, job) => {
  const code = job?.error_code || "";
  if (JOB_ERRORS.includes(code)) return t(`errors.${code}`);
  if (code === "internal_error") return t("exam_bank.word.job_error.internal_error");
  return job?.error_detail || t("exam_bank.word.job_error.unknown", { code: code || "—" });
};

const WARNINGS = [
  "heading_untranslated",
  "heading_missing",
  "option_letters",
  "options_dropped",
  "external_image",
  "image_unavailable",
  "image_unreadable",
  "unsupported_image",
  "image_too_large",
  "images_too_large",
  "local_file_image",
  "image_no_source",
  "pua_glyph",
  "katex_error",
  "math_as_text",
  "math_unavailable",
  "question_failed",
  "empty_section",
];
export const warningText = (t, item) =>
  WARNINGS.includes(item?.code)
    ? t(`exam_bank.word.warning.${item.code}`, { count: item.count, stored: item.stored, printed: item.printed })
    : t("exam_bank.word.warning.unknown", { code: item?.code || "" });

// The warnings that say where the page heading comes from: the test's page can set its own (its heading field).
export const HEADING_WARNINGS = ["heading_untranslated", "heading_missing"];

// Where in a question: its text, an option ("B жауабы"), the passage or the test's additional information.
export const partLabel = (t, part) => {
  if (!part) return "";
  if (part === "stem" || part === "passage" || part === "intro") return t(`exam_bank.word.where.part_${part}`);
  const option = /^option\s+(.+)$/.exec(part);
  return option ? t("exam_bank.word.where.part_option", { letter: option[1] }) : part;
};

const hasIndex = (value) => value !== null && value !== undefined && value !== "";

/**
 * The warnings by where they are: `sections[i]` = {own, questions} for the print's section i (own: about the test
 * itself, such as its heading; questions: [{number, items}] by question number, in their order), and `general`
 * (about the whole exam: all its pictures, the formula tool). empty_section is left out: the skipped tests say it.
 */
export const groupWarnings = (warnings) => {
  const sections = {};
  const general = [];
  (Array.isArray(warnings) ? warnings : []).forEach((item) => {
    if (!item || item.code === "empty_section") return;
    if (!hasIndex(item.section)) {
      general.push(item);
      return;
    }
    const group = (sections[item.section] = sections[item.section] || { own: [], questions: [] });
    if (!hasIndex(item.number)) {
      group.own.push(item);
      return;
    }
    let question = group.questions.find((entry) => entry.number === item.number);
    if (!question) {
      question = { number: item.number, items: [] };
      group.questions.push(question);
    }
    question.items.push(item);
  });
  Object.values(sections).forEach((group) => group.questions.sort((a, b) => Number(a.number) - Number(b.number)));
  return { sections, general };
};

// The number of warnings a teacher reads (the skipped tests are said apart).
export const warningCount = (warnings) =>
  (Array.isArray(warnings) ? warnings : []).filter((item) => item && item.code !== "empty_section").length;

// A key's download refused with 403 answers_hidden, in the page's words: its questions' answers are not the user's
// ("hidden"), or it was made before keys recorded their questions ("legacy": the Word is to be made again). The
// server's `reason` says which; without it (an older server) its Russian detail does. Null for any other refusal.
const LEGACY_DETAIL = /до проверки доступа/i;
export const keyRefusalText = (t, err) => {
  if (getApiErrorCode(err) !== "answers_hidden") return null;
  const data = err?.response?.data || {};
  const reason =
    data.reason === "hidden" || data.reason === "legacy"
      ? data.reason
      : typeof data.detail === "string" && LEGACY_DETAIL.test(data.detail)
        ? "legacy"
        : "hidden";
  return t(`exam_bank.documents.key_${reason}`);
};

// A size as the server writes it ("41.2 КБ"), in Latin units on the English page.
const LATIN_UNITS = { Б: "B", КБ: "KB", МБ: "MB", ГБ: "GB" };
export const sizeText = (size, language) => {
  if (!size || language !== "en") return size || "";
  return String(size).replace(/(\S+)$/, (unit) => LATIN_UNITS[unit] || unit);
};
